import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import Label from "@/v2-ui/label"
import Button from "@/v2-backoffice-client/components/button"
import UserTenantTypePicker from "@/v2-backoffice-client/user/tenant/type/UserTenantTypePicker"
import { useInvitationUpdateMutation } from "@/v2-backoffice-client/invitation/update/invitation.update.mutation"
import type { InvitationFragment } from "@/v2-backoffice-client/invitation/__types__/invitation.fragment"
import {
  MINUTE_LIMIT_OPTIONS,
  DEFAULT_MINUTE_LIMIT,
} from "@/v2-backoffice-client/invitation/invitation.constants"

export type InvitationUpdateDialogProps = DialogProps & {
  invitation: InvitationFragment
}

function InvitationUpdateDialog(props: InvitationUpdateDialogProps) {
  const { invitation, open, onOpenChange, ...dialogProps } = props

  const [userTenantTypeCode, setUserTenantTypeCode] = useState(
    invitation.UserTenantTypeCode,
  )
  const [minuteLimit, setMinuteLimit] = useState(DEFAULT_MINUTE_LIMIT)
  const [updateInvitation, { loading, error }] = useInvitationUpdateMutation({
    onCompleted() {
      onOpenChange?.(false)
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...dialogProps}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>Update Invitation</Dialog.Title>
          <Dialog.Description>{invitation.Email}</Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-4 text-center" error={error} />

        <form
          id="invitation-update-form"
          className="space-y-4 py-4"
          onSubmit={(e) => {
            e.preventDefault()
            updateInvitation({
              variables: {
                input: {
                  Id: invitation.Id,
                  UserTenantTypeCode: userTenantTypeCode,
                  MinuteLimit: minuteLimit,
                },
              },
            })
          }}
        >
          <div className="space-y-2">
            <Label>Role</Label>
            <UserTenantTypePicker
              className="w-full"
              align="between"
              variant="outline"
              size="sm"
              iconSrc="angle-small-down"
              iconPosition="right"
              iconClassName="text-muted-foreground"
              value={userTenantTypeCode}
              onChange={setUserTenantTypeCode}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="editInvitationExpiry">Expires in</Label>
            <select
              id="editInvitationExpiry"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={minuteLimit}
              onChange={(e) => {
                setMinuteLimit(Number.parseInt(e.target.value, 10))
              }}
            >
              {MINUTE_LIMIT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </form>

        <Dialog.Footer>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange?.(false)
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={loading}
            form="invitation-update-form"
          >
            Save
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default InvitationUpdateDialog
